import { useEffect, useState } from 'react'
import mockData from '../mock/mockData.json'
import type { DashboardData, PendingApplication } from '../data/types'
import { Header } from '../components/Header'
import { PendingApplicationsTable } from '../components/PendingApplicationsTable'
import { RecentActivity } from '../components/RecentActivity'
import { RegionOverview } from '../components/RegionOverview'
import { Sidebar } from '../components/Sidebar'
import { StatCards } from '../components/StatCards'
import { ReportsView } from './report/report_chart'
import { UserManagement } from './user/UserListPage'
import { LocationSetup } from './setup/location/LocationSetup'
import { LegalFormThreeTable } from './cases/Form3/LegalAdviceThreeTable'
import LegalCaseSearchPage from './SearchPage'
import { PLOFormOneTable } from './PLO/form1/PLOTable1'
import { PLOFormTwoTable } from './PLO/form2/PLOTable2'
import { LegalFormSevenTable } from './cases/Form7/LegalAdviceSevenTable'
import { LegalFormTenTable } from './cases/Form10/LegalAdviceTenTable'
import { LegalFormEightTable } from './cases/Form8/LegalAdviceEightTable'
import { PLOFormThreeTable } from './PLO/form3/PLOTableThree'
import { PLOFormFourTable } from './PLO/form4/PLOTableFour'
import { PLOFormFiveTable } from './PLO/form5/PLOTableFive'
import { PLOFormSixTable } from './PLO/form6/PLOTableSix'
import ComingSoon from '../components/ComingSoon'
import type { User } from '../types/user'
import Enable2FA from '../components/Enable2FA'
import { LegalFormOneTable } from './cases/Form1/LegalAdviceOneTable'
import { API_BASE_URL } from '../services/NetworkService'
import { LegalFormTwoTable } from './cases/Form2/LegalAdviceTwoTable'
import { LegalFormFourTable } from './cases/Form4/LegalAdviceFourTable'
import { LegalFormFiveTable } from './cases/Form5/LegalAdviceFiveTable'
import { LegalFormSixTable } from './cases/Form6/LegalAdviceSixTable'
import { LegalFormNineTable } from './cases/form9/LegalAdviceNineTable'
import { LegalFormElevenTable } from './cases/Form11/LegalAdviceElevenTable'
import { LegalFormSevenATable } from './cases/Form7/LegalAdviceSevenATable'
import { LegalFormEightATable } from './cases/Form8/LegalAdviceEightATable'

const initialMock = mockData as DashboardData

interface DashboardProps {
  onLogout: () => void
  activeTabId: string
  onTabChange: (id: string) => void
}

export function Dashboard({
  onLogout,
  activeTabId,
  onTabChange,
}: DashboardProps) {
  const [dashboardData, setDashboardData] = useState<DashboardData>(initialMock)
  const [applications, setApplications] = useState<PendingApplication[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)
  const [sidebarExpanded, setSidebarExpanded] = useState(true)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // 1. Fetch live backend data for the dashboard view
  useEffect(() => {
  let isMounted = true
  setIsLoading(true)
  setLoadError(null)

  const token = localStorage.getItem('access_token')

  fetch(`${API_BASE_URL}/documents/overview`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    },
  })
    .then((res) => {
      if (!res.ok) throw new Error('Failed to load dashboard data')
      return res.json()
    })
    .then((data: DashboardData) => {
      if (isMounted) {
        setDashboardData(data)
        setApplications(data.pendingApplications ?? [])
        setIsLoading(false)
      }
    })
    .catch((err) => {
      console.error('Failed to load live dashboard data:', err)
      if (isMounted) {
        setLoadError('Dashboard data could not be loaded. The information may be unavailable or out of date.')
        setIsLoading(false)
      }
    })

  return () => {
    isMounted = false
  }
}, [retryCount])

  // 2. Local user setup
  // 2. Local user setup
  const [currentUser] = useState<User>(() => {
    if (typeof window === 'undefined') {
      throw new Error('Dashboard requires an authenticated user.')
    }

    const storedUser = localStorage.getItem('user')
    if (!storedUser) {
      throw new Error('Authenticated user is missing from local storage.')
    }
    try {
      return JSON.parse(storedUser) as User
    } catch (error) {
      console.error('Failed to parse authenticated user from local storage:', error)
      throw error
    }
  })

  const currentActiveTab = activeTabId ?? 'dashboard'

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setMobileMenuOpen(false)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [mobileMenuOpen])

  // const handleView = useCallback((id: string) => {
  //   window.alert(`Opening application details for ${id}`)
  // }, [])

  // const handleApprove = useCallback((id: string) => {
  //   setApplications((prev) =>
  //     prev.map((app) => (app.id === id ? { ...app, status: 'Approved' } : app))
  //   )
  // }, [])

  // const handleReject = useCallback((id: string) => {
  //   setApplications((prev) =>
  //     prev.map((app) => (app.id === id ? { ...app, status: 'Returned' } : app))
  //   )
  // }, [])

  const activeOfficersCount = dashboardData.summaryStats.find(
    (stat) => stat.id === 'active-officers',
  )?.value
  const navigationItems = initialMock.navigation.map((item) =>
    item.id === 'users' && activeOfficersCount !== undefined
      ? { ...item, badge: String(activeOfficersCount) }
      : item,
  )


  return (
    <div className="flex h-screen w-screen overflow-hidden bg-slate-100">
      <Sidebar
        items={navigationItems}
        isExpanded={sidebarExpanded}
        isMobileOpen={mobileMenuOpen}
        activeItemId={currentActiveTab}
        onToggleExpand={() => setSidebarExpanded((prev) => !prev)}
        onCloseMobile={() => setMobileMenuOpen(false)}
        onNavigate={onTabChange}
      />

      <div className="flex min-w-0 flex-1 flex-col h-full overflow-hidden">
        <Header
          user={currentUser}
          onLogout={onLogout}
          onMenuOpen={() => setMobileMenuOpen(true)}
        />

        <main className="flex-1 overflow-y-auto px-4 py-6 sm:px-6 lg:px-8">
          {/* Dynamic Page Routing Shell */}
          {currentActiveTab === 'form-1' ? (
            <LegalFormOneTable />
          ) : currentActiveTab === 'form-2' ? (
            <LegalFormTwoTable />
          ) : currentActiveTab === 'form-3' ? (
            <LegalFormThreeTable />
          ) : currentActiveTab === 'form-4' ? (
            <LegalFormFourTable  />
          ) : currentActiveTab === 'form-5' ? (
            <LegalFormFiveTable />
          ) : currentActiveTab === 'form-6' ? (
            <LegalFormSixTable/>
          ) : currentActiveTab === 'form-7' ? (
            <LegalFormSevenTable/>
          ) : currentActiveTab === 'form-7a' ? (
            <LegalFormSevenATable />
          ) : currentActiveTab === 'form-8' ? (
            <LegalFormEightTable />
          ) : currentActiveTab === 'form-8a' ? (
            <LegalFormEightATable />
          ) : currentActiveTab === 'form-9' ? (
            <LegalFormNineTable/>
          ) : currentActiveTab === 'form-10' ? (
            <LegalFormTenTable />
          ) : currentActiveTab === 'form-11' ? (
            <LegalFormElevenTable/>
          ) : currentActiveTab === 'plo-form-1' ? (
            <PLOFormOneTable />
          ) : currentActiveTab === 'plo-form-2' ? (
            <PLOFormTwoTable />
          ) : currentActiveTab === 'plo-form-3' ? (
            <PLOFormThreeTable />
          ) : currentActiveTab === 'plo-form-4' ? (
            <PLOFormFourTable />
          ) : currentActiveTab === 'plo-form-5' ? (
            <PLOFormFiveTable />
          ) : currentActiveTab === 'plo-form-6' ? (
            <PLOFormSixTable />
          ) : currentActiveTab === 'legal-letter-1' ? (
            <ComingSoon />
          ) : currentActiveTab === 'legal-letter-2' ? (
            <ComingSoon />
          ) : currentActiveTab === 'legal-register-1' ? (
            <ComingSoon />
          ) : currentActiveTab === 'legal-register-2' ? (
            <ComingSoon />
          ) : currentActiveTab === 'approvals' ? (
            <ComingSoon />
          ) : currentActiveTab === 'users' ? (
            <UserManagement />
          ) : currentActiveTab === 'search' ? (
            <LegalCaseSearchPage />
          ) : currentActiveTab === 'locations' ? (
            <LocationSetup />
          ) : currentActiveTab === 'reports' ? (
            <ReportsView />
          ) : currentActiveTab === '2fa-enable' ? (
            <Enable2FA />
          ) : (
            <>
              {/* Default Main Dashboard View */}
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-slate-900">ဒက်ရှ်ဘုတ်</h2>
                <p className="mt-1 text-sm text-slate-500">
                  စာရင်းများ ခြုံငုံသုံးသပ်ခြင်း၊ စောင့်ဆိုင်းခြင်း၊ ဒေသအလိုက် ဆောင်ရွက်ချက်များ —{' '}
                  {new Date().toLocaleDateString('en-GB', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </p>
              </div>

              {isLoading ? (
                <div className="flex h-64 items-center justify-center">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
                </div>
              ) : loadError ? (
                <div
                  role="alert"
                  className="rounded-lg border border-red-200 bg-white p-6 text-center shadow-sm"
                >
                  <h3 className="text-base font-semibold text-slate-900">Dashboard unavailable</h3>
                  <p className="mt-2 text-sm text-slate-600">{loadError}</p>
                  <button
                    type="button"
                    onClick={() => setRetryCount((count) => count + 1)}
                    className="mt-4 rounded-md bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800"
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  <StatCards stats={dashboardData.summaryStats} />
                  <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
                    <div className="xl:col-span-2">
                      <PendingApplicationsTable
                        applications={applications}
                      />
                    </div>
                    <aside className="space-y-6">
                      <RegionOverview regions={dashboardData.regionOverview} />
                      <RecentActivity activities={dashboardData.recentActivity} />
                    </aside>
                  </div>
                </div>
              )}
            </>
          )}
        </main>

        {currentActiveTab === 'dashboard' && (
          <footer className="border-t border-slate-200 bg-white py-4">
            <div className="px-4 text-center text-xs text-slate-400 sm:px-6 lg:px-8">
              © {new Date().getFullYear()} — Legal Information Management System (LIMS) v1.0.0
            </div>
          </footer>
        )}
      </div>
    </div>
  )
}